import React, { useState } from 'react';
import { useMaterialTypes, getMaterialTypeIcon } from '../hooks/useMaterialTypes';

const ComplianceMaterialUpload = ({ 
  isOpen, 
  onClose, 
  onUpload,
  uploading = false 
}) => {
  const { materialTypes, loading: typesLoading } = useMaterialTypes();
  
  const [formData, setFormData] = useState({
    type: 'study_notes',
    title: '',
    description: '',
    file: null
  });
  
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [agreedToGuidelines, setAgreedToGuidelines] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.file || !formData.title || !formData.type || !agreedToGuidelines) {
      return;
    }
    
    onUpload(formData);
  };

  const handleClose = () => {
    if (uploading) return;
    
    // Reset form
    setFormData({
      type: 'study_notes',
      title: '',
      description: '',
      file: null
    });
    setAgreedToGuidelines(false);
    setShowGuidelines(false);
    onClose();
  };

  const selectedType = materialTypes[formData.type];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Upload Study Material
            </h2>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Academic Integrity Notice */}
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Academic Integrity Guidelines
                </h3>
                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                  <p>Only upload <strong>student-created</strong> materials. Prohibited content includes:</p>
                  <ul className="mt-1 list-disc list-inside space-y-0.5">
                    <li>Instructor slides, syllabi, or lecture materials</li>
                    <li>Copyrighted textbook content</li>
                    <li>Current assignment solutions or exam answers</li>
                    <li>Official course documents from faculty</li>
                  </ul>
                  <button 
                    onClick={() => setShowGuidelines(!showGuidelines)}
                    className="mt-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium text-sm underline"
                  >
                    {showGuidelines ? 'Hide' : 'View'} detailed guidelines
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Guidelines */}
          {showGuidelines && (
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">What You CAN Upload:</h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1 mb-4">
                <li>✅ Your personal study notes and summaries</li>
                <li>✅ Study guides you created for exam preparation</li>
                <li>✅ Practice problems you wrote or found online</li>
                <li>✅ Collaborative notes from study groups</li>
                <li>✅ Links to educational resources and references</li>
              </ul>
              
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">What You CANNOT Upload:</h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                <li>❌ Course syllabi or instructor materials</li>
                <li>❌ Professor's slides or lecture notes</li>
                <li>❌ Textbook pages or copyrighted content</li>
                <li>❌ Exam questions with official answers</li>
                <li>❌ Assignment solutions before due dates</li>
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Material Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Material Type *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {typesLoading ? (
                  <div className="col-span-2 text-center py-4 text-gray-500">
                    Loading material types...
                  </div>
                ) : (
                  Object.entries(materialTypes).map(([key, typeConfig]) => (
                    <div
                      key={key}
                      className={`relative rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                        formData.type === key
                          ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                      onClick={() => setFormData({ ...formData, type: key })}
                    >
                      <div className="flex items-start">
                        <div className={`flex-shrink-0 ${
                          formData.type === key ? 'text-red-600 dark:text-red-400' : 'text-gray-400'
                        }`}>
                          {getMaterialTypeIcon(key)}
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className={`text-sm font-medium ${
                            formData.type === key 
                              ? 'text-red-900 dark:text-red-200' 
                              : 'text-gray-900 dark:text-white'
                          }`}>
                            {typeConfig.label}
                          </div>
                          <div className={`text-xs ${
                            formData.type === key 
                              ? 'text-red-700 dark:text-red-300' 
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {typeConfig.description}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            formData.type === key
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {formData.type === key && (
                              <div className="w-full h-full rounded-full bg-white scale-50"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {selectedType && (
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  {selectedType.examples?.join(', ')}
                </p>
              )}
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Enter a descriptive title for your material"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                id="description"
                rows="3"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Optional: Add a description to help others understand your material"
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                File *
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <input
                  type="file"
                  id="file"
                  onChange={(e) => setFormData({ ...formData, file: e.target.files[0] })}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar"
                  required
                />
                <label htmlFor="file" className="cursor-pointer">
                  <div className="space-y-2">
                    <svg className="w-10 h-10 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {formData.file ? (
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formData.file.name}
                        </span>
                      ) : (
                        <>
                          <span className="font-medium text-red-600 dark:text-red-400">Click to upload</span>
                          <span> or drag and drop</span>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      PDF, DOC, PPT, images, and more (max 10MB)
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Guidelines Agreement */}
            <div className="flex items-start">
              <input
                type="checkbox"
                id="guidelines"
                checked={agreedToGuidelines}
                onChange={(e) => setAgreedToGuidelines(e.target.checked)}
                className="mt-1 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                required
              />
              <label htmlFor="guidelines" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                I confirm that this material is <strong>student-created</strong> and complies with academic integrity policies. 
                I understand that uploading prohibited content may result in account restrictions.
              </label>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleClose}
                disabled={uploading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading || !formData.file || !formData.title || !agreedToGuidelines}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:ring-2 focus:ring-red-500 ${
                  uploading || !formData.file || !formData.title || !agreedToGuidelines
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  'Upload Material'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComplianceMaterialUpload;